import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatAUD, formatErr } from "@/lib/api";
import { ArrowLeft, Download, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import AuthedImage from "@/components/AuthedImage";

export default function ProposalPreviewPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [project, setProject] = useState(null);
  const [profile, setProfile] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/projects/${id}`).then((r) => setProject(r.data)).catch(() => nav("/projects"));
    api.get("/profile").then((r) => setProfile(r.data));
  }, [id, nav]);

  const downloadPdf = async () => {
    setBusy(true);
    try {
      // ensure latest is generated
      await api.post(`/projects/${id}/proposal/pdf`);
      const res = await api.get(`/projects/${id}/proposal/pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${(project.title || "proposal").replace(/[^A-Za-z0-9._-]+/g, "-")}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error(formatErr(e) || "Could not generate the PDF");
    } finally { setBusy(false); }
  };

  if (!project || !profile) return <div className="p-12 text-muted-foreground">Loading…</div>;

  const t = project.totals || {};
  const today = new Date().toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" });

  return (
    <div className="bg-[color:hsl(var(--background-alt))] min-h-screen pb-20">
      {/* Top bar */}
      <div className="sticky top-0 z-20 bg-background/85 backdrop-blur border-b border-border" data-testid="proposal-toolbar">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => nav(`/projects/${id}`)} className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
            <ArrowLeft className="w-3 h-3" strokeWidth={1.5}/> Back to project
          </button>
          <div className="overline text-[10px]">Proposal Preview</div>
          <div className="flex gap-2">
            <button onClick={downloadPdf} disabled={busy} className="btn-primary" data-testid="btn-download-pdf">
              <Download className="w-4 h-4" strokeWidth={1.5}/> {busy ? "Preparing…" : "Download PDF"}
            </button>
          </div>
        </div>
      </div>

      {/* Paper */}
      <div className="max-w-4xl mx-auto px-4 pt-10">
        <div className="bg-[color:hsl(var(--paper))] shadow-2xl page-anim" style={{ minHeight: "1140px" }} data-testid="proposal-paper">
          {/* Cover */}
          <div className="p-12 lg:p-16">
            <div className="flex items-center justify-between border-b border-border pb-5 mb-10">
              <div>
                <div className="font-serif text-2xl">{profile.company_name}</div>
                <div className="overline text-[10px]" style={{ color: "#8A8A8A" }}>{profile.tagline}</div>
              </div>
              <div className="text-right">
                <div className="overline text-[10px]" style={{ color: "#8A8A8A" }}>Proposal</div>
                <div className="font-serif text-xl mt-1">No. {project.id.slice(-8).toUpperCase()}</div>
              </div>
            </div>

            <div className="overline">{project.room_type || "Custom"}</div>
            <h1 className="font-serif font-light mt-3" style={{ fontSize: "56px", lineHeight: 1.05 }} data-testid="proposal-title">
              {project.title}
            </h1>
            <p className="text-muted-foreground mt-4 max-w-xl">
              A bespoke media wall designed and crafted by {profile.company_name}. This document outlines the proposed materials, dimensions, schematic and investment for your project.
            </p>

            {/* Hero image */}
            <div className="aspect-[16/9] bg-muted mt-10 overflow-hidden">
              {project.images?.[0] ? (
                <AuthedImage path={project.images[0].storage_path} alt="" className="w-full h-full object-cover"/>
              ) : (
                <div className="w-full h-full flex items-center justify-center font-serif text-4xl text-[color:hsl(var(--accent))]">
                  {profile.company_name}
                </div>
              )}
            </div>

            <div className="grid grid-cols-3 mt-10 border-t border-border pt-6">
              <MetaCell label="Prepared For" value={project.client?.name || "—"}/>
              <MetaCell label="Prepared On" value={today}/>
              <MetaCell label="Status" value={project.status?.charAt(0).toUpperCase() + project.status?.slice(1)}/>
            </div>
          </div>

          {/* Overview */}
          <div className="border-t border-border p-12 lg:p-16">
            <div className="overline">Section 01</div>
            <h2 className="font-serif font-light mt-2" style={{ fontSize: "36px", lineHeight: 1.1 }}>Project Overview</h2>

            <div className="grid md:grid-cols-2 gap-6 mt-8">
              <DetailBox title="Prepared For">
                <div className="font-serif text-2xl mb-3">{project.client?.name || "—"}</div>
                <KV k="Email" v={project.client?.email || "—"}/>
                <KV k="Phone" v={project.client?.phone || "—"}/>
                <KV k="Address" v={project.client?.address || "—"}/>
              </DetailBox>
              <DetailBox title="Project Details">
                <div className="font-serif text-2xl mb-3">{project.title}</div>
                <KV k="Room type" v={project.room_type || "—"}/>
                <KV k="Wall width" v={`${project.wall_width_mm || 0} mm`}/>
                <KV k="Wall height" v={`${project.wall_height_mm || 0} mm`}/>
                <KV k="Wall depth" v={`${project.wall_depth_mm || 0} mm`}/>
              </DetailBox>
            </div>

            {project.notes && (
              <div className="mt-8 text-muted-foreground leading-relaxed">{project.notes}</div>
            )}
          </div>

          {/* Schematic */}
          <div className="border-t border-border p-12 lg:p-16">
            <div className="overline">Section 02</div>
            <h2 className="font-serif font-light mt-2" style={{ fontSize: "36px", lineHeight: 1.1 }}>Design & Schematic</h2>
            <p className="text-muted-foreground mt-3 mb-6">A scaled elevation view of your proposed media wall layout.</p>
            <SchematicPreview project={project}/>
          </div>

          {/* Materials */}
          <div className="border-t border-border p-12 lg:p-16">
            <div className="overline">Section 03</div>
            <h2 className="font-serif font-light mt-2" style={{ fontSize: "36px", lineHeight: 1.1 }}>Selected Materials</h2>
            <div className="grid md:grid-cols-2 gap-6 mt-8">
              {(project.line_items || []).slice(0, 8).map((li) => (
                <div key={li.id} className="luxe-card overflow-hidden" data-testid={`proposal-material-${li.id}`}>
                  <div className="aspect-[16/10] bg-muted">
                    <AuthedImage path={li.image_path} alt="" className="w-full h-full object-cover"/>
                  </div>
                  <div className="p-5">
                    <div className="overline text-[10px]">{li.category}</div>
                    <div className="font-serif text-xl mt-2">{li.name}</div>
                    <div className="text-xs text-muted-foreground mt-2 line-clamp-3">{li.description}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Gallery */}
          {project.images?.length > 1 && (
            <div className="border-t border-border p-12 lg:p-16">
              <div className="overline">Section 04</div>
              <h2 className="font-serif font-light mt-2" style={{ fontSize: "36px", lineHeight: 1.1 }}>Inspiration & References</h2>
              <div className="grid grid-cols-2 gap-3 mt-8">
                {project.images.map((img) => (
                  <div key={img.id} className="aspect-[4/3] bg-muted overflow-hidden">
                    <AuthedImage path={img.storage_path} alt="" className="w-full h-full object-cover"/>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Investment */}
          <div className="border-t border-border p-12 lg:p-16">
            <div className="overline">Section 05</div>
            <h2 className="font-serif font-light mt-2" style={{ fontSize: "36px", lineHeight: 1.1 }}>Investment</h2>

            <table className="w-full mt-8 text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 overline text-[10px]">Item</th>
                  <th className="text-right py-3 overline text-[10px]">Qty</th>
                  <th className="text-right py-3 overline text-[10px]">Unit</th>
                  <th className="text-right py-3 overline text-[10px]">Unit Price</th>
                  <th className="text-right py-3 overline text-[10px]">Line Total</th>
                </tr>
              </thead>
              <tbody>
                {(project.line_items || []).map((li) => (
                  <tr key={li.id} className="border-b border-border">
                    <td className="py-3">
                      <div className="font-medium">{li.name}</div>
                      <div className="text-xs text-muted-foreground">{li.category}</div>
                    </td>
                    <td className="py-3 text-right">{li.quantity}</td>
                    <td className="py-3 text-right text-muted-foreground">{li.unit}</td>
                    <td className="py-3 text-right">{formatAUD(li.unit_price)}</td>
                    <td className="py-3 text-right font-medium">{formatAUD(li.line_total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="mt-8 max-w-md ml-auto">
              <TotalRow k="Subtotal" v={formatAUD(t.subtotal)}/>
              <TotalRow k={`Labour (${(t.labour_rate*100).toFixed(1)}%)`} v={formatAUD(t.labour)}/>
              <TotalRow k={`Margin (${(t.margin_rate*100).toFixed(1)}%)`} v={formatAUD(t.margin)}/>
              <TotalRow k="Discount" v={`−${formatAUD(t.discount)}`}/>
              <TotalRow k={`GST (${(t.tax_rate*100).toFixed(1)}%)`} v={formatAUD(t.gst)}/>
              <div className="flex items-end justify-between pt-5 mt-3 border-t border-foreground">
                <div>
                  <div className="overline text-[10px]" style={{ color: "#A68A64" }}>Total (AUD inc. GST)</div>
                </div>
                <div className="font-serif font-light" style={{ fontSize: "36px" }} data-testid="proposal-total">{formatAUD(t.total)}</div>
              </div>
            </div>
          </div>

          {/* Terms */}
          <div className="border-t border-border p-12 lg:p-16">
            <div className="overline">Section 06</div>
            <h2 className="font-serif font-light mt-2" style={{ fontSize: "36px", lineHeight: 1.1 }}>Terms & Acceptance</h2>

            <div className="border-l-2 border-[color:hsl(var(--accent))] bg-[color:hsl(var(--surface-panel))] p-6 mt-6 text-sm text-muted-foreground leading-relaxed">
              This proposal is valid for thirty (30) days from the date of issue. All prices are quoted in Australian Dollars (AUD) and are inclusive of Goods and Services Tax (GST) where indicated. A 30% deposit is required to confirm the project and commence procurement. The remaining balance is payable upon practical completion. By signing below, the client accepts the scope, design and investment outlined in this proposal.
            </div>

            <div className="grid grid-cols-2 gap-10 mt-10">
              <div>
                <div className="border-b border-foreground h-12"></div>
                <div className="overline text-[10px] mt-2" style={{ color: "#A68A64" }}>Client Signature</div>
              </div>
              <div>
                <div className="border-b border-foreground h-12"></div>
                <div className="overline text-[10px] mt-2" style={{ color: "#A68A64" }}>Date</div>
              </div>
            </div>

            <div className="mt-12 text-center">
              <div className="font-serif text-3xl">{profile.company_name}</div>
              <div className="overline text-[10px] mt-2" style={{ color: "#8A8A8A" }}>{profile.tagline}</div>
              <div className="text-xs text-muted-foreground mt-3">
                {[profile.email, profile.phone, profile.address].filter(Boolean).join(" · ")}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function MetaCell({ label, value }) {
  return (
    <div>
      <div className="overline text-[10px]" style={{ color: "#A68A64" }}>{label}</div>
      <div className="mt-2">{value}</div>
    </div>
  );
}
function DetailBox({ title, children }) {
  return (
    <div className="bg-[color:hsl(var(--surface-panel))] p-6 border border-border">
      <div className="overline text-[10px]" style={{ color: "#A68A64" }}>{title}</div>
      <div className="mt-3">{children}</div>
    </div>
  );
}
function KV({ k, v }) {
  return (
    <div className="flex justify-between border-b border-border py-2 last:border-0 text-sm">
      <span className="text-muted-foreground">{k}</span>
      <span className="font-medium">{v}</span>
    </div>
  );
}
function TotalRow({ k, v }) {
  return (
    <div className="flex justify-between py-2 border-b border-border text-sm">
      <span className="text-muted-foreground">{k}</span>
      <span className="font-medium">{v}</span>
    </div>
  );
}

function SchematicPreview({ project }) {
  const items = project.schematic?.items || [];
  const wallW = project.wall_width_mm || 3000;
  const wallH = project.wall_height_mm || 2400;
  const TYPE_COLORS = {
    tv: "#1C1C1C", fireplace: "#A68A64", cabinet: "#5C5C5C",
    shelf: "#8A8A8A", feature_panel: "#3A4D39", lighting: "#B8860B",
  };
  const pad = 30;
  const viewW = 800;
  const scale = (viewW - pad * 2) / wallW;
  const viewH = wallH * scale + pad * 2;

  return (
    <div className="schematic-bg p-6 border border-border" data-testid="proposal-schematic">
      <svg viewBox={`0 0 ${viewW} ${viewH}`} className="w-full">
        <rect x={pad} y={pad} width={wallW*scale} height={wallH*scale} fill="#FAF9F5" stroke="#1C1C1C" strokeWidth={2}/>
        {items.map((it) => (
          <g key={it.id}>
            <rect
              x={pad + it.x*scale}
              y={pad + it.y*scale}
              width={it.width*scale}
              height={it.height*scale}
              fill={TYPE_COLORS[it.type] || "#5C5C5C"}
              fillOpacity={0.9}
              stroke="#1C1C1C"
            />
            {it.width*scale > 50 && it.height*scale > 20 && (
              <text
                x={pad + (it.x + it.width/2)*scale}
                y={pad + (it.y + it.height/2)*scale + 4}
                fill="#FFFFFF"
                fontFamily="Manrope"
                fontSize={11}
                fontWeight={600}
                textAnchor="middle">
                {(it.type || "").replace("_", " ").toUpperCase()}
              </text>
            )}
          </g>
        ))}
        <text x={pad + wallW*scale/2} y={viewH - 8} fill="#5C5C5C" fontFamily="Manrope" fontSize={10} textAnchor="middle">{wallW} mm wide</text>
      </svg>
      {items.length === 0 && (
        <div className="text-xs text-muted-foreground mt-4">No elements added yet.</div>
      )}
    </div>
  );
}
